import axios, { HttpStatusCode } from 'axios';
import React from 'react';
import { useParams } from 'react-router-dom';
import { Checkbox } from '@chakra-ui/react';
import { useAlertContext } from '../../../../../../context/AlertProvider';
import { useApi } from '../../../../../../context/ApiProvider';
import CustomAlert from '../../../../../CustomAlert/CustomAlert';
import IconComponent from '../../../../../IconComponent/IconComponent';
import { KidInterface } from '../../../../../../shared/type/kid.type';

interface Props {
  isOpen: boolean;
  allChildren: KidInterface[];
  onCancel: () => void;
  onConfirm: () => void;
  afterAction?: () => void;
  selectedChildIds: number[];
  setSelectedChildIds: React.Dispatch<React.SetStateAction<number[]>>;
}

const DeleteKid = ({
  isOpen,
  allChildren,
  onCancel,
  onConfirm,
  afterAction,
  selectedChildIds,
  setSelectedChildIds
}: Props) => {
  const { apiTenantConsumerController } = useApi();
  const { setShow, setAlertProperties } = useAlertContext();
  const { id: tenantId } = useParams<{ id: string }>();

  const handleToggleChild = (id: number) => {
    setSelectedChildIds((prev) =>
      prev.includes(id) ? prev.filter((pid) => pid !== id) : [...prev, id]
    );
  };

  return (
    <CustomAlert
      handleOpen={isOpen}
      icon={
        <div className="w-12 h-12 flex items-center justify-center rounded-full bg-error-100">
          <IconComponent iconName="trash" />
        </div>
      }
      header="Usunąć zaznaczonych konsumentów?"
      content={
        <div className="flex flex-col gap-spacing-xl">
          <p className="font-normal text-sm">
            Usunięcie uczniów spawia, że trafiają do archiwum.
          </p>
          <ul className="list-inside text-sm text-grayLight-900">
            {allChildren
              .filter((child) => selectedChildIds.includes(child.id))
              .map((child) => (
                <li key={child.id}>
                  <Checkbox
                    isChecked={selectedChildIds.includes(child.id)}
                    onChange={() => handleToggleChild(child.id)}
                    colorScheme="customOrange">
                    {child.firstName} {child.lastName}
                  </Checkbox>
                </li>
              ))}
          </ul>
        </div>
      }
      cancelButton={{
        iconPosition: 'onlyText',
        size: 'lg',
        hierarchy: 'secondaryGray'
      }}
      cancelButtonText="Anuluj"
      confirmButton={{
        iconPosition: 'onlyText',
        size: 'lg',
        hierarchy: 'warning'
      }}
      confirmButtonText="Usuń"
      onConfirmAction={async () => {
        try {
          for (const id of selectedChildIds) {
            const response = await apiTenantConsumerController('').delete(`/${tenantId}/${id}`);
            if (response.status !== HttpStatusCode.Ok) {
              throw new Error(`Failed to delete kid with id ${id}`);
            }
          }

          setAlertProperties({
            timeout: 9000,
            title: 'Sukces',
            description: 'Poprawnie usunięto konsumentów',
            status: 'success'
          });
          setShow(true);
          onConfirm();
          afterAction?.();
        } catch (error) {
          if (axios.isAxiosError(error) && error.response) {
            setAlertProperties({
              timeout: 9000,
              title: 'Błąd usuwania',
              description: `Błąd: ${error.response.data}`,
              status: 'warning'
            });
          } else {
            setAlertProperties({
              timeout: 9000,
              title: 'Błąd',
              description: `Nie udało się usunąć konsumentów`,
              status: 'error'
            });
          }
          setShow(true);
        }
      }}
      onCancelAction={onCancel}
    />
  );
};

export default DeleteKid;
